// Single authoritative source for all Vibealone legal documents.
// Content transcribed verbatim from the supplied "Privacy Policy.pdf"
// (extraction ligature artifacts corrected: fi/fl pairs).
// All documents are marked in the source as: Last updated June 2026 · Version 1.0 (draft for review)

export const DOC_META = "Last updated June 2026 · Version 1.0 (draft for review)";

export const DRAFT_NOTICE = {
  heading: "Operator and legal information",
  paragraphs: [
    "Vibealone is currently operated by an individual founder and is not represented as an incorporated company.",
    "For privacy questions, account-deletion requests, or complaints, contact support@vibealone.com. We will review enquiries and respond as appropriate.",
    "Vibealone's formal business details and applicable grievance arrangements are being finalised. This notice does not replace any disclosures or appointments required by applicable law.",
  ],
};

export const SUPPORT_EMAIL = "support@vibealone.com";

export const LEGAL_DOCS = [
  {
    slug: "/privacy",
    navLabel: "Privacy Policy",
    title: "Privacy Policy",
    meta: DOC_META,
    intro:
      "This policy explains what personal data Vibealone collects, why, where it is stored, who it is shared with and the choices you have. It describes the app as it works today; anything planned but not yet built is labelled as such. This policy describes the Vibealone app; this informational website has no accounts or sign-in of its own, and its use of browser technologies is described in the Cookie Policy.",
    sections: [
      {
        heading: "Who we are",
        blocks: [
          { t: "p", text: "Vibealone is a social commerce app where people share experiences at places and brands, and where cafés and businesses can take orders and reservations." },
          { t: "p", text: "Vibealone is currently operated by its founding team as an unincorporated startup. The legal operating entity, its registered address and a named Grievance Officer have not yet been confirmed and will be added here before this document is finalised. This draft is pending review by a qualified Indian legal professional." },
        ],
      },
      {
        heading: "Data you give us",
        blocks: [
          {
            t: "list",
            items: [
              { lead: "Account:", text: "email address, full name, display name, username, age (creators), password (stored only as a one-way bcrypt hash), bio and profile photo." },
              { lead: "Sign in with Google:", text: "your email address, name and profile picture, received through the Emergent-managed Google sign-in service." },
              { lead: "Sign in with Apple:", text: "a private Apple user identifier, and the email address and name if you choose to share them (Apple may provide a relay email)." },
              { lead: "Experiences:", text: "photos you upload, captions, the place you tag (chosen from a list or search, not your device's GPS location), likes, and messages you send in place chat rooms. When you delete a post, it and its photo are removed from Vibealone, including from the place's photo gallery." },
              { lead: "Orders:", text: "the café, items, prices, totals, a pickup code, order status and the payment method the café records (cash or UPI). Payment happens at the café; Vibealone does not process card or UPI payments." },
              { lead: "Reservations and bookings:", text: "the venue, date, time, number of guests and any note you add." },
              { lead: "Brand accounts:", text: "business name, category, description, logo, cover image, address, city, keywords, opening hours, store details and verification status." },
              { lead: "Trust & safety:", text: "reports you file (reason and the content or user reported) and the users you block." },
            ],
          },
        ],
      },
      {
        heading: "Data created when you use Vibealone",
        blocks: [
          {
            t: "list",
            items: [
              { lead: "Post views:", text: "when a post is viewed we record the post, its author, the signed-in viewer's account ID (or, for signed-out visitors, a random session identifier) and the time. Views are counted at most once per hour per viewer and shown to the creator as aggregate analytics." },
              { lead: "Attribution:", text: "if you order after opening a creator's post, the order records which post led to it so the creator sees aggregate impact." },
              { lead: "Sessions:", text: "sign-in tokens so you stay signed in." },
            ],
          },
          { t: "p", text: "Our hosting infrastructure records operational logs that can include your IP address and request details (such as time, requested address and device or browser information), for security and reliability. Our hosting provider keeps these logs for up to about 30 days." },
        ],
      },
      {
        heading: "Device permissions",
        blocks: [
          { t: "p", text: "Camera and photo library access are requested only when you choose to add a photo, and are used only to attach that photo to your post or profile. You can withdraw access at any time in your device settings." },
        ],
      },
      {
        heading: "Why we use your data",
        blocks: [
          { t: "p", text: "Under India's Digital Personal Data Protection Act, 2023 we process your data on the basis of the consent you give when you create an account and use specific features, and for legitimate uses permitted by the Act. You may withdraw consent at any time by deleting your account; this does not affect processing that already took place." },
          {
            t: "list",
            items: [
              "To create and secure your account and keep you signed in.",
              "To show your posts, profile and activity to other users as you intend.",
              "To pass your order or reservation to the café or brand you chose, so they can fulfil it.",
              "To show creators and brands aggregate analytics about their posts and orders.",
              "To review brand applications (Founder Approval) before a business can operate on Vibealone.",
              "To keep the community safe: moderating content, handling reports and honouring blocks.",
              "To comply with legal obligations.",
            ],
          },
        ],
      },
      {
        heading: "Who we share data with",
        blocks: [
          { t: "p", text: "We do not sell personal data. We have not integrated advertising networks or third-party analytics or tracking SDKs. Links to delivery and ride services (such as Zomato, Swiggy and Uber) open those services, whose own privacy policies apply." },
          {
            t: "list",
            items: [
              { lead: "Other users:", text: "your public profile (name, username, photo, bio), posts and chat messages are visible to other users." },
              { lead: "Cafés and brands:", text: "when you order or reserve, that business sees your order or reservation details and your public profile." },
              { lead: "Emergent:", text: "provides the Google sign-in service and the platform used to build and host Vibealone." },
              { lead: "Apple and Google:", text: "when you choose to sign in with them." },
              { lead: "OpenStreetMap (Nominatim):", text: "the place-search text you type is sent to OpenStreetMap's search service to find locations." },
              { lead: "Content delivery services:", text: "maps (OpenStreetMap tiles, Leaflet via unpkg), fonts and icons (jsDelivr) and default avatars (DiceBear, which receives your username to draw a placeholder avatar) are loaded from third-party servers, which receive your IP address as part of normal web requests." },
              "Authorities, where required by law.",
            ],
          },
        ],
      },
      {
        heading: "Where data is stored",
        blocks: [
          { t: "p", text: "Account data, posts (including photos), orders, reservations, analytics and safety records are stored in a MongoDB Atlas database hosted on Google Cloud in the United States (us-central1). The Vibealone servers run on Amazon Web Services in the United States (us-west-2). This means your personal data is transferred to, and processed in, the United States. Where Indian law restricts transfers to particular countries, we will follow those restrictions. The legal basis for and safeguards around these transfers are pending review by a qualified Indian legal professional." },
        ],
      },
      {
        heading: "How long we keep data",
        blocks: [
          { t: "p", text: "Our hosting provider keeps automatic database backups: snapshots every 6 hours kept for 7 days, daily snapshots kept for 7 days, weekly snapshots kept for 4 weeks, monthly snapshots kept for 12 months and yearly snapshots kept for 1 year, plus a rolling 7-day point-in-time recovery window. Data deleted from the live database can therefore remain in backup copies for up to about 12 months, until those backups expire. Backups are used only to recover from failures and are not used to restore deleted accounts. Our provider reports that media files are not included in database backups. Fixed retention periods for de-identified order and reservation records have not yet been set." },
          {
            t: "list",
            items: [
              "Your account and content: until you delete them or your account.",
              "Google sign-in sessions expire after 7 days; email sign-in tokens expire after 30 days.",
              "After account deletion, order and reservation records are kept by the café without any link to you (see Delete Account), for their business records.",
              "Safety reports are kept with your identity removed, to help prevent abuse.",
              "A minimal internal audit entry recording that an account was deleted is kept for security.",
            ],
          },
        ],
      },
      {
        heading: "Your rights",
        blocks: [
          { t: "p", text: "Requests to access a copy of your data or to correct data you cannot edit yourself are currently handled manually by email." },
          {
            t: "list",
            items: [
              { lead: "Access and correction:", text: "view and edit your profile in the app (Settings → Edit profile)." },
              { lead: "Erasure:", text: "delete your account and associated data at any time in the app (Settings → Delete account)." },
              { lead: "Withdraw consent:", text: "delete your account, or revoke device permissions in your phone settings." },
              { lead: "Grievance redressal:", text: "contact us (see Contact & Support). If you are not satisfied, you may complain to the Data Protection Board of India once its procedures apply to you." },
              "Nominate a person to exercise your rights in case of death or incapacity, by contacting us.",
            ],
          },
        ],
      },
      {
        heading: "Age requirement",
        blocks: [
          { t: "p", text: "Vibealone is only for people aged 18 or older. Creators declare their age and confirm they are 18+ when signing up; accounts that sign in with Google or Apple must confirm they are 18+ before they can post, order, reserve, like or chat. We do not knowingly process the data of children. If you believe a child is using Vibealone, contact us and we will delete the account." },
        ],
      },
      {
        heading: "Security",
        blocks: [
          { t: "p", text: "Data travels between your device and our servers over encrypted HTTPS connections, and our hosting providers encrypt stored data at rest. We also store passwords only as one-way hashes, restrict which websites may call our servers, and limit repeated attempts on sensitive actions. These measures reduce risk but cannot eliminate it, and no system is completely secure. On the web version, your sign-in token is stored in your browser's local storage; we are evaluating more secure storage." },
        ],
      },
      {
        heading: "Indian law",
        blocks: [
          { t: "p", text: "Vibealone is built in India. We aim to comply with the Information Technology Act, 2000, the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021, and the Digital Personal Data Protection Act, 2023 together with the Digital Personal Data Protection Rules, 2025, which were notified in November 2025 and take effect in phases. How these apply to Vibealone is pending legal review." },
        ],
      },
      {
        heading: "Changes to this policy",
        blocks: [
          { t: "p", text: "We will update this page and the date below when our practices change, and tell you in the app about material changes." },
        ],
      },
    ],
  },
  {
    slug: "/terms",
    navLabel: "Terms & Conditions",
    title: "Terms & Conditions",
    meta: DOC_META,
    intro:
      "These terms govern your use of Vibealone. By creating an account or using the app you agree to them. Please also read our Privacy Policy and Community Guidelines.",
    sections: [
      {
        heading: "About these terms",
        blocks: [
          { t: "p", text: "Vibealone is currently operated by its founding team as an unincorporated startup. The legal operating entity, its registered address and a named Grievance Officer have not yet been confirmed and will be added here before this document is finalised. This draft is pending review by a qualified Indian legal professional." },
        ],
      },
      {
        heading: "Eligibility",
        blocks: [
          {
            t: "list",
            items: [
              "You must be at least 18 years old.",
              "You must give accurate information and keep it up to date.",
              "Brand accounts must be created by someone authorised to act for that business.",
            ],
          },
        ],
      },
      {
        heading: "Your account",
        blocks: [
          { t: "p", text: "You are responsible for keeping your sign-in details safe and for activity on your account. Tell us promptly if you believe your account has been compromised. You may delete your account at any time." },
        ],
      },
      {
        heading: "Your content",
        blocks: [
          { t: "p", text: "You keep ownership of the photos, captions and messages you post. You grant Vibealone a non-exclusive, worldwide, royalty-free licence to host, display and distribute that content within Vibealone, and to show it to brands tagged in it, for as long as it remains on the service." },
          { t: "p", text: "You confirm you have the rights to everything you post and that it does not infringe anyone else's rights or break the law or our Community Guidelines." },
        ],
      },
      {
        heading: "Prohibited conduct",
        blocks: [
          {
            t: "list",
            items: [
              "Breaking the law or helping others to.",
              "Harassment, hate, threats, impersonation, fraud, spam or manipulating engagement and analytics.",
              "Posting content you have no right to share, or sexual content involving minors (reported to authorities).",
              "Interfering with, scraping or reverse-engineering the service, or attempting to access other accounts.",
            ],
          },
        ],
      },
      {
        heading: "Brands and Founder Approval",
        blocks: [
          { t: "p", text: "Businesses apply through brand onboarding. Every brand is reviewed by the Vibealone founding team before it can access Brand OS, list menus, accept orders or take reservations. We may approve, reject or later suspend a brand at our discretion, for example for inaccurate information or breaches of these terms." },
          { t: "p", text: "Brands are responsible for their own listings, prices, menus, hours, food safety, taxes and compliance with laws that apply to their business." },
        ],
      },
      {
        heading: "Orders, reservations and payments",
        blocks: [
          { t: "p", text: "Orders and reservations made through Vibealone are agreements between you and the café or brand. Vibealone provides the tool that passes your request to them." },
          { t: "p", text: "Orders are paid at the café, in person (for example cash or UPI). Vibealone does not take payment in the app." },
          { t: "p", text: "You show the café your pickup code to collect an order. Reservations are confirmed when a slot is available and can be cancelled in the app." },
          { t: "p", text: "Refunds, no-shows and disputes about food or service are handled by the café under its own policies. Where we can, we will help you get in touch." },
        ],
      },
      {
        heading: "Third-party services",
        blocks: [
          { t: "p", text: "Vibealone links to and relies on other services (such as Apple, Google, OpenStreetMap, Zomato, Swiggy and Uber). Their terms apply when you use them, and we are not responsible for them." },
        ],
      },
      {
        heading: "Suspension and termination",
        blocks: [
          { t: "p", text: "We may remove content, restrict features or suspend or close accounts that break these terms or the Community Guidelines, or where we must do so by law. You can stop using Vibealone and delete your account at any time." },
        ],
      },
      {
        heading: "Disclaimers and liability",
        blocks: [
          { t: "p", text: "Vibealone is provided \"as is\". We do not guarantee that it will always be available or error-free, or that information posted by users or brands is accurate. To the extent permitted by law, we are not liable for indirect or consequential loss, or for the acts of users, cafés or brands. Nothing in these terms limits liability that cannot be limited under Indian law, including under the Consumer Protection Act, 2019." },
          { t: "p", text: "The limitation of liability, governing law and jurisdiction wording is pending review by an Indian legal professional." },
        ],
      },
      {
        heading: "Disputes and governing law",
        blocks: [
          { t: "p", text: "These terms are governed by the laws of India. Please contact us first so we can try to resolve any issue informally. Jurisdiction details will be confirmed once the operating entity is confirmed." },
        ],
      },
      {
        heading: "Changes",
        blocks: [
          { t: "p", text: "We may update these terms. We will change the date below and tell you in the app about material changes. Continuing to use Vibealone after changes take effect means you accept them." },
        ],
      },
      {
        heading: "Contact",
        blocks: [
          { t: "p", text: "Our contact address is support@vibealone.com. Response times may vary." },
        ],
      },
    ],
  },
  {
    slug: "/community-guidelines",
    navLabel: "Community Guidelines",
    title: "Community Guidelines",
    meta: DOC_META,
    intro:
      "Vibealone is for sharing real experiences at real places. These guidelines keep it honest, kind and safe.",
    sections: [
      {
        heading: "Share",
        blocks: [
          {
            t: "list",
            items: [
              "Your own photos and honest experiences of places, food and brands.",
              "Content you have the right to post, with the consent of people clearly shown in it.",
              "Fair, respectful opinions, including critical ones.",
            ],
          },
        ],
      },
      {
        heading: "Don't post",
        blocks: [
          {
            t: "list",
            items: [
              "Harassment, bullying, threats or hate based on identity.",
              "Impersonation of people or businesses, or misleading claims about who you are.",
              "Fraud, scams, fake reviews, paid engagement you have not disclosed, or attempts to game views and analytics.",
              "Spam or repetitive promotional content.",
              "Nudity, sexual content, graphic violence, or content encouraging self-harm or dangerous acts.",
              "Anything involving minors in a sexual or exploitative way. We will remove it and report it to authorities.",
              "Illegal goods or services, or other people's private information.",
            ],
          },
        ],
      },
      {
        heading: "Report and block",
        blocks: [
          {
            t: "list",
            items: [
              { lead: "Report a post or user:", text: "tap the ••• menu on a post and choose Report. Pick a reason and submit." },
              { lead: "Block a user:", text: "from the same menu choose Block. Their posts are hidden from your feed and they cannot message you. You can unblock later." },
              "Brands can hide or report posts on their community page.",
              "Report a wider problem by email (see Contact & Support).",
            ],
          },
        ],
      },
      {
        heading: "How we handle reports",
        blocks: [
          { t: "p", text: "New posts may be held for review before they appear publicly; while held, only you can see them. Reports are reviewed by the Vibealone team, who may remove content, restrict features or suspend accounts. Reports are kept confidential; the reported person is not told who reported them." },
          { t: "p", text: "We review reports as quickly as we can, but we do not currently guarantee a specific response time." },
        ],
      },
      {
        heading: "Disagree with a decision?",
        blocks: [
          { t: "p", text: "There is no in-app appeals flow yet. If you think we got something wrong, email us with the details and we will take another look." },
        ],
      },
    ],
  },
  {
    slug: "/delete-account",
    navLabel: "Account Deletion",
    title: "Delete Account",
    meta: DOC_META,
    intro:
      "You can permanently delete your Vibealone account and associated data at any time, directly in the app.",
    sections: [
      {
        heading: null,
        blocks: [
          { t: "p", text: "Sign in to delete my account", strong: true },
          { t: "p", text: "Deletion requires signing in so we can verify the account is yours.", small: true },
          { t: "p", text: "Note: this website is informational only and has no account system of its own. These instructions apply to accounts created in the Vibealone app.", small: true },
        ],
      },
      {
        heading: "How to delete",
        blocks: [
          { t: "p", text: "If you cannot access the app, email us from the address on your account with the subject \"Delete my account\" and we will process your request manually after verifying it is you." },
          {
            t: "ol",
            items: [
              "Open Vibealone and sign in.",
              "Go to Profile → Settings → Delete account.",
              "Type DELETE to confirm and tap Permanently delete account.",
            ],
          },
        ],
      },
      {
        heading: "What is deleted",
        blocks: [
          {
            t: "list",
            items: [
              "Your account: name, email, username, age, password hash, bio and profile photo.",
              "All of your posts, including their photos and captions.",
              "View analytics for your posts, and records of posts you viewed.",
              "Your chat messages, likes, place check-ins and block list.",
              "Every signed-in session on every device.",
              "Brand accounts: pending verification reviews and activation tokens.",
            ],
          },
        ],
      },
      {
        heading: "What is kept, and why",
        blocks: [
          {
            t: "list",
            items: [
              { lead: "Orders:", text: "the café keeps the transaction (items, amounts, date, status) for its business and tax records. The link to your account is removed." },
              { lead: "Reservations and bookings:", text: "kept by the venue with the link to your account and your note removed." },
              { lead: "Safety reports you filed or that were filed about you:", text: "kept with your identity removed, to help prevent abuse." },
              "An internal security record that an account was deleted.",
              "Copies in our hosting provider's database backups, which expire on a schedule and may persist for up to about 12 months. Backups are not used to restore deleted accounts.",
            ],
          },
        ],
      },
      {
        heading: "Sign in with Apple",
        blocks: [
          { t: "p", text: "If you signed in with Apple, deletion removes your Vibealone account. Automatic revocation of your Apple sign-in token is not yet active; it will be enabled once Apple credentials are configured. Until then you can stop Vibealone using your Apple ID in your iPhone's Settings → [your name] → Sign-In & Security → Sign in with Apple." },
        ],
      },
      {
        heading: "Deletion is permanent",
        blocks: [
          { t: "p", text: "Deleted accounts cannot be restored. You may create a new account later, but your previous content will not return." },
        ],
      },
    ],
  },
  {
    slug: "/cookies",
    navLabel: "Cookie Policy",
    title: "Cookie Policy",
    meta: DOC_META,
    intro:
      "This policy explains the cookies and similar technologies used by the Vibealone website.",
    sections: [
      {
        heading: "Cookies",
        blocks: [
          { t: "p", text: "This website does not set any cookies. It is an informational website with no login, account, shopping cart or preference features that would require them." },
        ],
      },
      {
        heading: "Browser storage",
        blocks: [
          { t: "p", text: "This website does not use your browser's local storage or session storage. Nothing you do on this website is saved in your browser by us." },
        ],
      },
      {
        heading: "Analytics and advertising",
        blocks: [
          { t: "p", text: "We do not run our own analytics, advertising or tracking scripts or pixels on this website." },
        ],
      },
      {
        heading: "Third-party content and hosting",
        blocks: [
          { t: "p", text: "All fonts and images on this website are served from our own hosting, so simply browsing these pages does not load maps, social-media embeds or other third-party content. This website is hosted on the Emergent platform; as part of serving the site, Emergent's hosting and preview infrastructure may load its own tooling scripts from Emergent domains. These platform scripts are controlled by Emergent, are governed by Emergent's own policies, and — like any web server — the hosting infrastructure processes standard request information such as your IP address." },
        ],
      },
      {
        heading: "The Vibealone app",
        blocks: [
          { t: "p", text: "The Vibealone app is a separate product from this website. The app's use of browser storage (for example, to keep you signed in on its web version) applies only when you use the app, not when you browse this website." },
        ],
      },
      {
        heading: "Your choices",
        blocks: [
          { t: "p", text: "Because this website does not set cookies or use browser storage, no cookie consent banner is needed. If we ever add non-essential cookies or analytics to this website, we will update this policy and ask for your consent first." },
        ],
      },
    ],
  },
  {
    slug: "/contact",
    navLabel: "Contact & Support",
    title: "Contact & Support",
    meta: DOC_META,
    intro:
      "How to reach the Vibealone team for help, privacy requests and safety concerns.",
    sections: [
      {
        heading: "Email",
        blocks: [
          { t: "p", text: "Our contact address is support@vibealone.com. Response times may vary." },
          { t: "p", text: "Please use the subject lines below so your message reaches the right place." },
          {
            t: "list",
            items: [
              { lead: "General support:", text: "\"Support request\"" },
              { lead: "Privacy or data request (access, correction, nomination):", text: "\"Privacy request\"" },
              { lead: "Account deletion if you cannot use the app:", text: "\"Delete my account\"" },
              { lead: "Safety concern or report:", text: "\"Safety report\"" },
              { lead: "Brand and business questions:", text: "\"Brand support\"" },
            ],
          },
        ],
      },
      {
        heading: "In the app",
        blocks: [
          {
            t: "list",
            items: [
              "Report a post or block a user from the ••• menu on any post.",
              "Delete your account in Settings → Delete account.",
              "Edit your profile in Settings → Edit profile.",
            ],
          },
        ],
      },
      {
        heading: "Grievance Officer",
        blocks: [
          { t: "p", text: "A named Grievance Officer, as required under Indian law, has not yet been appointed. Until then, please send grievances to the email above with the subject \"Grievance\"." },
          { t: "p", text: "Vibealone is currently operated by its founding team as an unincorporated startup. The legal operating entity, its registered address and a named Grievance Officer have not yet been confirmed and will be added here before this document is finalised. This draft is pending review by a qualified Indian legal professional." },
        ],
      },
      {
        heading: null,
        blocks: [
          { t: "email", text: "Questions? support@vibealone.com" },
        ],
      },
    ],
  },
];
